import { useState, useEffect, useRef } from "react";
import { signInWithPopup } from "firebase/auth";
import { auth, googleProvider, isFirebaseConfigured } from "../../config/firebase";
import { useAuthStore } from "../../store/useStore";
import { Btn, Divider } from "../ui";
import { isValidEmail, validatePassword, validateName } from "../../utils/validation";
import { trapFocus, announceToScreenReader } from "../../utils/accessibility";

interface AuthModalProps {
  mode: "signin" | "signup";
  onClose: () => void;
  onSwitch: (mode: "signin" | "signup") => void;
}

export function AuthModal({ mode, onClose, onSwitch }: AuthModalProps) {
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [name, setName]         = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const { login, loginWithGoogle, isLoading, error, clearError } = useAuthStore();
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const cleanup = trapFocus(modalRef.current!);
    return cleanup;
  }, []);

  useEffect(() => {
    announceToScreenReader(`${mode === 'signin' ? 'Sign in' : 'Create account'} dialog opened`);
  }, [mode]);

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!isValidEmail(email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (mode === 'signup') {
      const nameValidation = validateName(name);
      if (!nameValidation.isValid) {
        errors.name = nameValidation.error!;
      }
    }
    
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) {
      errors.password = passwordValidation.errors[0];
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    clearError();
    
    if (!validateForm()) {
      return;
    }
    
    await login(email || "demo@gamestore.io", password || "demo");
    if (!useAuthStore.getState().error) onClose();
  };

  const handleGoogleSignIn = async () => {
    clearError();
    
    if (!isFirebaseConfigured) {
      // Show a user-friendly message when Firebase is not configured
      setFieldErrors({ 
        firebase: "Google Sign-In is not available. Please configure Firebase to enable this feature." 
      });
      return;
    }
    
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const idToken = await result.user.getIdToken();
      await loginWithGoogle(idToken);
      if (!useAuthStore.getState().error) onClose();
    } catch (e) {
      console.error("Google Sign-In error:", e);
      // Show a user-friendly error message
      setFieldErrors({ 
        firebase: "Google Sign-In failed. Please try again or use email sign-in." 
      });
    }
  };

  const OAUTH_PROVIDERS = [
    { 
      id: "google", 
      icon: "🇬", 
      label: "Continue with Google", 
      handler: handleGoogleSignIn,
      disabled: !isFirebaseConfigured 
    },
  ];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-title"
      aria-describedby="auth-description"
      style={{
        position: "fixed", inset: 0, zIndex: 100,
        background: "var(--bg-modal)", backdropFilter: "blur(8px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        ref={modalRef}
        style={{
          background: "var(--bg-surface)", border: "1px solid var(--border)",
          borderRadius: "var(--radius-xl)", padding: "36px 40px",
          width: "100%", maxWidth: 420,
          boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ fontFamily: "var(--font-display)", fontSize: 28, fontWeight: 900, marginBottom: 6 }}>
            <span style={{ color: "var(--accent-bright)" }}>GS</span>
            <span style={{ color: "var(--text-muted)" }}>STORE</span>
          </div>
          <h2 id="auth-title" style={{ fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5 }}>
            {mode === "signin" ? "Welcome Back" : "Create Account"}
          </h2>
          <p id="auth-description" style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 6 }}>
            {mode === "signin" ? "Sign in to access your library and wishlist" : "Join millions of players on GameStore"}
          </p>
        </div>

        {/* OAuth */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 22 }}>
          {OAUTH_PROVIDERS.map(({ id, icon, label, handler, disabled }) => (
            <button
              key={id}
              className="btn btn-ghost"
              style={{ 
                width: "100%", 
                justifyContent: "center", 
                gap: 10, 
                padding: "11px", 
                fontSize: 14,
                opacity: disabled ? 0.6 : 1,
                cursor: disabled ? "not-allowed" : "pointer"
              }}
              onClick={handler}
              disabled={isLoading || disabled}
              title={disabled ? "Google Sign-In is not configured" : undefined}
            >
              <span style={{ fontSize: 16 }}>{icon}</span>
              {label}
            </button>
          ))}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 22 }}>
          <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
          <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600 }}>OR</span>
          <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
        </div>

        {/* Form */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 }}>
          {mode === "signup" && (
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.8, display: "block", marginBottom: 6 }}>Full Name</label>
              <input 
                className="gs-input" 
                placeholder="Your name" 
                value={name} 
                onChange={(e) => setName(e.target.value)}
                style={{ borderColor: fieldErrors.name ? "var(--color-wishlist)" : undefined }}
              />
              {fieldErrors.name && (
                <div style={{ fontSize: 11, color: "var(--color-wishlist)", marginTop: 4 }}>
                  {fieldErrors.name}
                </div>
              )}
            </div>
          )}
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.8, display: "block", marginBottom: 6 }}>Email</label>
            <input 
              className="gs-input" 
              type="email" 
              placeholder="you@email.com" 
              value={email} 
              onChange={(e) => setEmail(e.target.value)}
              style={{ borderColor: fieldErrors.email ? "var(--color-wishlist)" : undefined }}
            />
            {fieldErrors.email && (
              <div style={{ fontSize: 11, color: "var(--color-wishlist)", marginTop: 4 }}>
                {fieldErrors.email}
              </div>
            )}
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.8, display: "block", marginBottom: 6 }}>Password</label>
            <input 
              className="gs-input" 
              type="password" 
              placeholder="••••••••" 
              value={password} 
              onChange={(e) => setPassword(e.target.value)}
              style={{ borderColor: fieldErrors.password ? "var(--color-wishlist)" : undefined }}
            />
            {fieldErrors.password && (
              <div style={{ fontSize: 11, color: "var(--color-wishlist)", marginTop: 4 }}>
                {fieldErrors.password}
              </div>
            )}
          </div>
        </div>

        {error && (
          <div style={{ background: "rgba(255,77,109,0.1)", border: "1px solid rgba(255,77,109,0.3)", borderRadius: "var(--radius-sm)", padding: "10px 14px", fontSize: 13, color: "var(--color-wishlist)", marginBottom: 14 }}>
            {error}
          </div>
        )}

        {fieldErrors.firebase && (
          <div style={{ background: "rgba(255,165,0,0.1)", border: "1px solid rgba(255,165,0,0.3)", borderRadius: "var(--radius-sm)", padding: "10px 14px", fontSize: 13, color: "var(--color-wishlist)", marginBottom: 14 }}>
            {fieldErrors.firebase}
          </div>
        )}

        {mode === "signin" && (
          <div style={{ textAlign: "right", marginBottom: 16 }}>
            <span style={{ fontSize: 13, color: "var(--text-accent)", cursor: "pointer", fontWeight: 600 }}>Forgot password?</span>
          </div>
        )}

        <Btn fullWidth size="lg" onClick={handleSubmit} disabled={isLoading} style={{ marginBottom: 16 }}>
          {isLoading ? "Please wait…" : mode === "signin" ? "Sign In" : "Create Account"}
        </Btn>

        <p style={{ textAlign: "center", fontSize: 13, color: "var(--text-muted)" }}>
          {mode === "signin" ? "New to GameStore?" : "Already have an account?"}
          {" "}
          <span
            style={{ color: "var(--text-accent)", cursor: "pointer", fontWeight: 600 }}
            onClick={() => onSwitch(mode === "signin" ? "signup" : "signin")}
          >
            {mode === "signin" ? "Create Account" : "Sign In"}
          </span>
        </p>

        <Divider style={{ margin: "18px 0 0" }} />
        <p style={{ textAlign: "center", fontSize: 11, color: "var(--text-muted)", marginTop: 12, lineHeight: 1.6 }}>
          By continuing you agree to our Terms of Service and Privacy Policy. GDPR / data rights apply.
        </p>
      </div>
    </div>
  );
}
